import { AI_VOICES, AI_VOICE_MODELS } from '../../../common/enums/enums';
import {
    Badge,
    Box,
    Button,
    FileInput,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    TagsInput,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { getMediaGroups, getUserMediaGroups } from '../../../../http/apiClient';
import { useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchableSelect } from '../../../common';
import { countVariations } from '../../../../utils/text';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import useAddMessage from '../../../../http/mutations/groups/useAddMessage';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';

const SmsParentMessageForm = ({ form }) => {
    const SubView = useMemo(() => {
        switch (form.values.type) {
            case 'Sms':
                return SmsMessageForm;

            case 'Mms':
                return MmsMessageForm;
        }
    }, [form.values.type]);

    return (
        <>
            <Select
                label={'Type'}
                data={['Sms', 'Mms']}
                key={form.key('type')}
                {...form.getInputProps('type')}
            />
            {SubView ? <SubView form={form} /> : null}
        </>
    );
};

const SmsMessageForm = ({ form }) => {
    return (
        <>
            <MessageContentInput
                form={form}
                withAsterisk
            />

            <TextInput
                label={'Tracking ID'}
                placeholder='Enter a tracking id'
                {...form.getInputProps('trackingId')}
            />

            <TagsInput
                label={'Tags'}
                placeholder={'Tags'}
                key={form.key('tags')}
                {...form.getInputProps('tags')}
            />
        </>
    );
};

const MMS_TYPE_OPTIONS = [
    { label: 'Text-only', value: 'Text' },
    { label: 'Image and Text', value: 'ImageText' },
    { label: 'Image-only', value: 'Image' },
    { label: 'Audio and Text', value: 'AudioText' },
    { label: 'Audio-only', value: 'Audio' },
    { label: 'Video and Text', value: 'VideoText' },
    { label: 'Video-only', value: 'Video' },
];

const MmsMessageForm = ({ form }) => {
    const userProfile = useAuthStore((state) => state.userProfile);

    const [currentType, setCurrentType] = useState(form.values.mmsOptions.type);
    const [showState, setShowState] = useState({});
    useEffect(() => {
        const showMessageContent = currentType?.includes('Text');
        const showImageSelect = currentType?.includes('Image');
        const showVideoInput = currentType?.includes('Video');
        const showAudioInput = currentType?.includes('Audio');
        setShowState({
            showMessageContent,
            showImageSelect,
            showVideoInput,
            showAudioInput,
        });
    }, [currentType]);

    const {
        showMessageContent,
        showImageSelect,
        showVideoInput,
        showAudioInput,
    } = showState;

    const { onChange, ...typeInputProps } =
        form.getInputProps('mmsOptions.type');

    return (
        <>
            <Select
                label={'MMS Type'}
                data={MMS_TYPE_OPTIONS}
                withAsterisk
                key={form.key('mmsOptions.type')}
                {...typeInputProps}
                onChange={(e) => {
                    setCurrentType(e);
                    onChange(e);
                }}
            />

            {showImageSelect ? (
                <SearchableSelect
                    label={'Media Group'}
                    placeholder='Select media group'
                    queryHttpFn={(queryParams) => {
                        const userId = form.values.userId;
                        const searchUserVerticals =
                            hasRole(userProfile, 'SUPER_ADMIN') && userId;

                        if (searchUserVerticals) {
                            return getUserMediaGroups({
                                userId,
                                pagination: queryParams,
                            });
                        }
                        return getMediaGroups(queryParams);
                    }}
                    queryKey={'media_groups'}
                    optionsMapFn={(x) => ({
                        label: x.name,
                        value: x.id?.toString(),
                    })}
                    withAsterisk
                    key={form.key('mmsOptions.mediaGroupId')}
                    {...form.getInputProps('mmsOptions.mediaGroupId')}
                />
            ) : null}

            {showAudioInput ? (
                <FileInput
                    label={'Audio File'}
                    accept={'audio/*'}
                    placeholder={'Select an audio file'}
                    withAsterisk
                    {...form.getInputProps('file')}
                />
            ) : null}

            {showVideoInput ? (
                <FileInput
                    label={'Video File'}
                    accept={'video/*'}
                    placeholder={'Select a video file'}
                    withAsterisk
                    {...form.getInputProps('file')}
                />
            ) : null}

            {showMessageContent ? (
                <MessageContentInput
                    form={form}
                    withAsterisk
                />
            ) : null}

            <TextInput
                label={'Tracking ID'}
                placeholder='Enter a tracking id'
                {...form.getInputProps('trackingId')}
            />

            <TagsInput
                label={'Tags'}
                placeholder={'Tags'}
                key={form.key('tags')}
                {...form.getInputProps('tags')}
            />
        </>
    );
};

const RvmMessageForm = ({ form }) => {
    return (
        <>
            <Select
                label={'AI Voice Model'}
                data={AI_VOICE_MODELS}
                withAsterisk
                {...form.getInputProps('rvmOptions.model')}
            />

            <Select
                label={'AI Character'}
                data={AI_VOICES}
                withAsterisk
                {...form.getInputProps('rvmOptions.character')}
            />

            <MessageContentInput form={form} />
        </>
    );
};

const EmailMessageForm = ({ form }) => {
    return (
        <>
            <MessageContentInput
                form={form}
                label={'Email Plaintext'}
                placeholder={'Enter plaintext email template'}
                withAsterisk
            />

            <MessageContentInput
                form={form}
                name={'emailOptions.html'}
                label={'Email HTML'}
                placeholder={'Enter HTML email template'}
            />

            <TextInput
                label={'Tracking ID'}
                placeholder='Enter a tracking id'
                {...form.getInputProps('trackingId')}
            />

            <TagsInput
                label={'Tags'}
                placeholder={'Tags'}
                key={form.key('tags')}
                {...form.getInputProps('tags')}
            />
        </>
    );
};

const MessageContentInput = ({ form, ...extraProps }) => {
    const {
        name = 'rawContent',
        label = 'Message Content',
        placeholder = 'Enter message content',
        ...rest
    } = extraProps;

    const messageVariation = useMemo(() => {
        if (!form.values.rawContent) {
            return 0;
        }
        return countVariations(form.values.rawContent);
    }, [form.values[name]]);

    return (
        <Textarea
            autosize
            minRows={5}
            maxRows={20}
            inputWrapperOrder={['label', 'input', 'error', 'description']}
            label={label}
            placeholder={placeholder}
            description={`Variations: ${messageVariation}`}
            {...rest}
            {...form.getInputProps(name)}
        />
    );
};

const EditMessageModal = ({ messageGroup, existing, opened, close }) => {
    const userProfile = useAuthStore((state) => state.userProfile);

    const getInitialValues = () => {
        if (!existing) {
            return {
                id: null,
                groupId: messageGroup.id,
                userId: hasRole(userProfile, 'SUPER_ADMIN')
                    ? userProfile?.id.toString()
                    : null,
                rawContent: '',
                type: 'Sms',

                mmsOptions: {
                    type: 'Text',
                },
                rvmOptions: {
                    model: 'Default',
                    character: 'Default',
                },
                emailOptions: {
                    html: '',
                },
                vertical: '',
            };
        }

        return {
            id: existing.id,
            groupId: messageGroup.id,
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? existing.userId
                : null,
            type: existing.type,
            mmsOptions: {
                type: 'Text',
            },
            rvmOptions: {
                model: existing?.rvmOptions?.model || 'Default',
                character: existing?.rvmOptions?.character || 'Default',
            },
            emailOptions: {
                html: existing?.emailOptions?.html,
            },
            rawContent: existing.rawMessage || 'test',
            trackingId: existing.trackingId,
            tags: existing.tags,
        };
    };
    const form = useForm({
        validateInputOnBlur: true,
        initialValues: getInitialValues(),

        validate: {
            'rawContent': (value, values) => {
                if (values.type === 'Mms') {
                    const showMessageContent =
                        values.mmsOptions?.type?.includes('Text');

                    if (!showMessageContent) {
                        return;
                    }
                }
                const { type, message } = genericValidator(
                    'Message content',
                    value,
                    {
                        required: true,
                    }
                );
                if (type) {
                    return type === 'pattern' ? 'Invalid link' : message;
                }
            },

            'rvmOptions.model': (value, values) => {
                const { message } = genericValidator('Model', value, {
                    required: true,
                });
                return message;
            },

            'rvmOptions.character': (value, values) => {
                const { message } = genericValidator('Character', value, {
                    required: true,
                });
                return message;
            },

            'emailOptions.html': (value, values) => {
                return;
            },

            'trackingId': (value) => {
                const { type, message } = genericValidator(
                    'Tracking ID',
                    value,
                    {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Tracking ID must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },

            'tags': (values) => {
                if (!values) {
                    return;
                }

                for (const value of values) {
                    const { type, message } = genericValidator('Tags', value, {
                        required: false,
                        minLength: 2,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    });
                    if (type) {
                        return type === 'pattern'
                            ? 'Tags must only contain letters, numbers, hyphens and underscores'
                            : message;
                    }
                }
            },
        },
    });

    useEffect(() => {
        const initialValues = getInitialValues();
        form.setInitialValues(initialValues);
        form.reset();
    }, [existing, opened]);

    const { isPending, mutate: addOrUpdate } = useAddMessage();

    const SubView = useMemo(() => {
        switch (messageGroup?.type?.toLowerCase()) {
            case 'sms':
            default:
                return SmsParentMessageForm;
            case 'rvm':
                return RvmMessageForm;
            case 'email':
                //default:
                return EmailMessageForm;
        }
    }, [messageGroup]);

    const onSubmit = (model) => {
        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>
                        {existing ? 'Update' : 'Add'} Message
                    </Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            {SubView ? <SubView form={form} /> : null}

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={isPending || !form.isValid()}
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    {existing ? 'Update' : 'Create'}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default EditMessageModal;
